import { TitleStatus, Vehicle } from '@otw/models/inventory'

const TITLE_STATUS_ORDER: TitleStatus[] = [
  'purchased_for_inventory',
  'bank_information',
  'payment_sent',
  'title_requested',
  'in_transit',
  'physical_title_received',
  'title_flip_required',
  'title_flip_not_required',
  'title_flip_in_progress',
  'title_flip_completed',
  'shipping_information',
  'title_shipped',
  'completed',
]

export type VehicleFinancialToggleUpdate = {
  paid_for?: boolean
  paid_for_date?: string | null
  funded?: boolean
  funded_date?: string | null
  title_tracking?: Vehicle['title_tracking']
  ready_for_transport?: Vehicle['ready_for_transport']
}

function hasFullPickupAddress(vehicle: Vehicle): boolean {
  const pickup = vehicle.pickup_location
  return Boolean(
    pickup?.address?.trim() &&
      pickup?.city?.trim() &&
      pickup?.state?.trim() &&
      pickup?.zip?.trim(),
  )
}

export function buildPaidForUpdate(
  vehicle: Vehicle,
  newValue: boolean,
  updatedBy?: string,
): VehicleFinancialToggleUpdate {
  const hasFullPickupAddressValue = hasFullPickupAddress(vehicle)
  const updateData: VehicleFinancialToggleUpdate = { paid_for: newValue }

  const currentReadyForTransport = vehicle.ready_for_transport || {
    needs_address: false,
    ready_for_pickup: false,
  }
  const shouldBeReadyForPickup = Boolean(newValue && hasFullPickupAddressValue)
  updateData.ready_for_transport = {
    ...currentReadyForTransport,
    ready_for_pickup: shouldBeReadyForPickup,
    needs_address: shouldBeReadyForPickup
      ? false
      : currentReadyForTransport.needs_address || false,
    ready_for_transport_date: shouldBeReadyForPickup
      ? currentReadyForTransport.ready_for_transport_date || new Date().toISOString()
      : currentReadyForTransport.ready_for_transport_date,
  }

  if (newValue) {
    updateData.paid_for_date = new Date().toISOString()

    const currentTitleStatus = vehicle.title_tracking?.title_status
    const currentStatusIndex = currentTitleStatus ? TITLE_STATUS_ORDER.indexOf(currentTitleStatus) : -1
    const paymentSentIndex = TITLE_STATUS_ORDER.indexOf('payment_sent')

    if (
      vehicle.purchased_for_inventory === true &&
      (currentStatusIndex === -1 || currentStatusIndex < paymentSentIndex) &&
      currentTitleStatus !== 'payment_sent'
    ) {
      const existingTitleTracking = vehicle.title_tracking || {}
      updateData.title_tracking = {
        ...existingTitleTracking,
        title_status: 'payment_sent',
        paid_date: updateData.paid_for_date,
        last_updated_at: new Date().toISOString(),
        last_updated_by: updatedBy || 'System',
      }
    }
  } else {
    updateData.paid_for_date = null
  }

  return updateData
}

/** Clears the ready-for-pickup mark and its date. Paid trucks stay on Needs transport. */
export function buildUnmarkReadyForPickupUpdate(
  vehicle: Pick<Vehicle, 'ready_for_transport'>,
): VehicleFinancialToggleUpdate {
  const currentReadyForTransport = vehicle.ready_for_transport || {
    needs_address: false,
    ready_for_pickup: false,
  }

  return {
    ready_for_transport: {
      ...currentReadyForTransport,
      ready_for_pickup: false,
      ready_for_transport_date: null,
    },
  }
}

export function buildFundedUpdate(newValue: boolean): VehicleFinancialToggleUpdate {
  if (newValue) {
    return {
      funded: true,
      funded_date: new Date().toISOString(),
    }
  }

  return {
    funded: false,
    funded_date: null,
  }
}
